import {
    Box,
    Heading,
    HStack,
    VStack,
    Text,
    Flex,
    Alert,
    AlertIcon,
    Button,
    Icon,
    IconButton,
    Tooltip,
    Input,
    useToast,
    Spinner,
    Center,
    Badge,
    Menu,
    MenuButton,
    MenuList,
    MenuItem,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { useState, useRef, useEffect, useLayoutEffect, useContext, useMemo, useCallback } from 'react';
import { useParams, useLocation, useSearchParams } from 'react-router-dom';
import { useForceSidebarCollapsed } from '../../contexts/SidebarCollapseOverrideContext';
import {
    TbCopy,
    TbTruck,
    TbPencil,
    TbMessageCircle,
    TbFlag,
    TbAlertTriangle,
    TbChevronRight,
    TbChevronLeft,
    TbSparkles,
    TbBellOff,
} from 'react-icons/tb';
import { HiOutlineDotsVertical } from 'react-icons/hi';
import { FiCheckCircle, FiFileText, FiDollarSign, FiShoppingCart, FiPackage, FiRefreshCw, FiPercent, FiClock } from 'react-icons/fi';
import { CheckboxMenuRow, Icon as YaxleIcon, IconButton as YaxleIconButton, MenuItemRow } from '../../ui';
import { exportErrorMessage, saveAndPrintPdf, supplierOrdersOnOrder, useExportSupplierOrderSummary } from './hooks/useExportSupplierOrderSummary';
import { FaExclamationTriangle } from 'react-icons/fa';
import { ORDER_ACTION_ICONS } from '../../utils/orderActionIcons';
import OrderChatDrawer from '../../modals/OrderChatDrawer';
import CarlChatDrawer from './modals/CarlChatDrawer';
import ActivityLogDrawer, { EVENT_TYPE_COLORS, EVENT_TYPE_ICON_BG, EventTypeIcon } from './modals/ActivityLogDrawer';
import OrderNotificationsDrawer from './modals/OrderNotificationsDrawer';
import ExportHistoryDrawer from './modals/ExportHistoryDrawer';
import SupplierNotesModal, { SupplierOrderSummary } from '../../modals/SupplierNotesModal';
import { useGetOrderDetail } from '../../hooks/useGetOrderDetail';
import { invalidateOrder } from '../../hooks/invalidateOrder';
import { useOrderCollaboration } from '../../hooks/useOrderCollaboration';
import { useGetNotifications, isNotificationTypeMuted } from '../../hooks/useGetNotifications';
import { getNotificationIcon, getNotificationItemMessage } from '../../components/notifications/NotificationRow';
import { useRecentActivity } from '../../hooks/useRecentActivity';
import { useArchiveOrder } from '../../hooks/useArchiveOrder';
import { useCompleteOrder } from '../../hooks/useCompleteOrder';
import { useUpdateOrderDetails } from '../../hooks/useUpdateOrderDetails';
import { QuotingTabs, SelectionTabs, OrderedTabs, NewPartsSection, RekeyTabs } from '../../components/order';
import SelectionFullScreen from '../../components/order/SelectionFullScreen';
import OrderedFullScreen from '../../components/order/OrderedFullScreen';
import QuotingFullScreen from '../../components/order/QuotingFullScreen';
import { formatPrice } from '../../functions';
import GrossMarginSummaryValue from '../../components/GrossMarginSummaryValue';
import OrderTag from '../../components/OrderTag';
import { EnterEstimateBadge } from '../../components/orders/components';
import RefreshButton from '../../components/RefreshButton';
import SessionContext from '../../contexts/SessionContext';
import { canUseAskCarl } from '../../utils/askCarlAccess';
import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { useTableSettings } from '../../hooks/useTableSettings';
import TableSettingsButton from '../../components/TableSettingsButton';
import OrderWorkflowOverlay from '../../components/OrderWorkflowOverlay';
import useCopyToClipboard from '../../hooks/useCopyToClipboard';
import CopyTooltip from '../../components/CopyTooltip';
import OrderCollaborationAvatars from '../../components/order/OrderCollaborationAvatars';
import { brandCopy } from '../../constants/brand';
import { getActivitySourceLabel } from '../../components/SourceBadge';

const EVENT_TYPE_DISPLAY_LABELS: Record<string, string> = {
    'order-created': 'Order Created',
    'part-excluded': 'Part Excluded',
    'part-included': 'Part Included',
    'part-marked-ordered': 'Part Marked Ordered',
    'part-unmarked-ordered': 'Part Marked Unordered',
    'part-excluded-from-quoting': 'Excluded From Quoting',
    'part-quoting-restored': 'Quoting Restored',
    'quotes-complete': 'Quoting Complete',
    'part-selections-changed': 'Changed Selections',
    'list-price-adjusted': 'List Price Adjusted',
    'option-note-changed': 'Option Note Changed',
    'orders-complete': 'Order Placed',
    'order-completed': 'Order Completed',
    'order-uncompleted': 'Order Reopened',
    'order-archived': 'Order Archived',
    'order-unarchived': 'Order Unarchived',
    //older order types no longer used, but left to support events produced before 04/16/26
    'auto-order': 'Auto Order',
    'request-quotes': 'Request Quotes',
    'place-order': 'Order Placed',
    'workflow-cancelled': 'Workflow Cancelled',
};

type OrderStep = 'newParts' | 'quoting' | 'selection' | 'ordered' | 'rekey';
type Tab = 'home' | 'todo' | 'activity' | 'dashboard' | 'flagged' | 'invoices' | 'profile' | 'settings' | 'delivery-board' | 'inventory';

/** Thin scrollbar styling shared by the equal-height sidebar sections. */
// Scrollable but with the scrollbar hidden — the activity/notification previews
// stay scrollable while keeping a clean, chrome-free look.
const SIDEBAR_SCROLLBAR_SX = {
    '&::-webkit-scrollbar': { display: 'none' },
    scrollbarWidth: 'none' as const,
    msOverflowStyle: 'none' as const,
};

/* ─────────────────────────────────────────────────────── */
/* Small helper: sidebar label / value row                 */
/* ─────────────────────────────────────────────────────── */
const SidebarField = ({ label, children }: { label: string; children: React.ReactNode; }) => (
    <Box>
        <Text fontSize="10px" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider" mb={0.5}>
            {label}
        </Text>
        {children}
    </Box>
);

const getEtaRange = (etaValues: string[]): string | null => {
    const now = new Date();
    const currentYear = now.getFullYear();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    const parsedEtas = etaValues
        .map((eta) => eta.trim())
        .filter(Boolean)
        .map((eta) => {
            const match = eta.match(/(\d{1,2})\/(\d{1,2})/);
            if (!match) {
                return null;
            }

            const month = Number(match[1]);
            const day = Number(match[2]);
            if (!month || !day) {
                return null;
            }

            let parsedDate = new Date(currentYear, month - 1, day);
            if (parsedDate < thirtyDaysAgo) {
                parsedDate = new Date(currentYear + 1, month - 1, day);
            }

            return { eta, parsedDate };
        })
        .filter((value): value is { eta: string; parsedDate: Date; } => value !== null)
        .sort((a, b) => a.parsedDate.getTime() - b.parsedDate.getTime());

    if (parsedEtas.length === 0) {
        return null;
    }

    const earliestEta = parsedEtas[0].eta;
    const latestEta = parsedEtas[parsedEtas.length - 1].eta;

    return earliestEta === latestEta ? earliestEta : `${earliestEta} - ${latestEta}`;
};

const STEPPER_SEGMENT_S = 0.22;

/** Stagger connector fills so skipped steps light up one after another, not all at once. */
const stepperLineDelayS = (idx: number, fromIdx: number, toIdx: number) => {
    if (toIdx > fromIdx && idx >= fromIdx && idx < toIdx) {
        return (idx - fromIdx) * STEPPER_SEGMENT_S;
    }
    if (toIdx < fromIdx && idx >= toIdx && idx < fromIdx) {
        return (fromIdx - 1 - idx) * STEPPER_SEGMENT_S;
    }
    return 0;
};

const OrderDetail = () => {
    const { id } = useParams<{ id: string; }>();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    // Support both navigation state and URL query param for tab selection
    const tabFromQuery = searchParams.get('tab') as OrderStep | null;
    const initialStatus = (location.state?.initialStatus || tabFromQuery) as OrderStep | undefined;
    const [selectedStatus, setSelectedStatus] = useState<OrderStep>(initialStatus || 'newParts');
    const prevSelectedStatusRef = useRef<OrderStep>(selectedStatus);
    useLayoutEffect(() => {
        prevSelectedStatusRef.current = selectedStatus;
    }, [selectedStatus]);
    const [isEditingRONumber, setIsEditingRONumber] = useState(false);
    const [roNumberValue, setRONumberValue] = useState('');
    // Only one side drawer may be open at a time. Tracking the active drawer in a
    // single state (rather than four independent booleans) makes them mutually
    // exclusive by construction: opening one silently unmounts whichever was open,
    // so closing never cascades through a stack of overlapping drawers.
    const [activeDrawer, setActiveDrawer] = useState<'chat' | 'supplierNotes' | 'activity' | 'notifications' | 'emsHistory' | null>(null);
    const [exportPanel, setExportPanel] = useState<'menu' | 'scope' | 'pick'>('menu');
    const [summarySelection, setSummarySelection] = useState<string[]>([]);
    const [isExportingSummary, setIsExportingSummary] = useState(false);
    const { exportSummary } = useExportSupplierOrderSummary();
    const isChatOpen = activeDrawer === 'chat';
    const isSupplierNotesOpen = activeDrawer === 'supplierNotes';
    const isActivityLogOpen = activeDrawer === 'activity';
    const isOrderNotificationsOpen = activeDrawer === 'notifications';
    const isEmsHistoryOpen = activeDrawer === 'emsHistory';
    const [chatComposeMode, setChatComposeMode] = useState<'message' | 'note'>('message');
    const [isCarlDrawerOpen, setIsCarlDrawerOpen] = useState(false);
    const [supplierNotesInitialTab, setSupplierNotesInitialTab] = useState<'quoting-comments' | 'order-messages'>('quoting-comments');
    const [supplierNotesInitialSupplierOrderId, setSupplierNotesInitialSupplierOrderId] = useState<string | null>(null);
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    // Any right-side drawer being open (order chat, Ask Carl, supplier notes,
    // activity log, notifications, or export history) should collapse the RHS
    // customer-info / recent-activity / notifications sidebar so the drawer and
    // the LHS content get the horizontal space. We derive an "effective" visible
    // state instead of mutating `isSidebarOpen`, so the user's own open/closed
    // toggle is preserved and restored once every drawer is closed.
    const isAnyDrawerOpen = activeDrawer !== null || isCarlDrawerOpen;
    // Collapse the app left SidebarMenu while any order drawer is open (Carl,
    // chat, supplier notes, activity, notifications, export history). Preference
    // in localStorage is left alone and restored when every drawer closes.
    useForceSidebarCollapsed(isAnyDrawerOpen);
    const isSidebarVisible = isSidebarOpen && !isAnyDrawerOpen;
    // The collapsed sidebar rail is only clickable to re-open when the user
    // collapsed it themselves — while a drawer forces it collapsed the sidebar is
    // fully hidden (width 0), so there's no rail to interact with.
    const isSidebarRailInteractive = !isSidebarVisible && !isAnyDrawerOpen;
    // Collapse/expand driven by a drawer opening or closing must be instant so the
    // RHS panel stays in lockstep with the (instant) drawer and the wrapper's
    // instant width shift — animating only the sidebar looked broken. The manual
    // chevron toggle keeps its smooth animation. We suppress the sidebar's own
    // transition for the single frame in which a drawer toggle flips visibility.
    const [suppressSidebarTransition, setSuppressSidebarTransition] = useState(false);
    const prevAnyDrawerOpenRef = useRef(isAnyDrawerOpen);
    useLayoutEffect(() => {
        if (prevAnyDrawerOpenRef.current === isAnyDrawerOpen) return;
        prevAnyDrawerOpenRef.current = isAnyDrawerOpen;
        setSuppressSidebarTransition(true);
        const raf = requestAnimationFrame(() => setSuppressSidebarTransition(false));
        return () => cancelAnimationFrame(raf);
    }, [isAnyDrawerOpen]);
    const [isSelectionFullScreen, setIsSelectionFullScreen] = useState(false);
    const [isOrderedFullScreen, setIsOrderedFullScreen] = useState(false);
    const [isQuotingFullScreen, setIsQuotingFullScreen] = useState(false);
    const toast = useToast();
    const queryClient = useQueryClient();
    const hasSetInitialStatus = useRef(false);
    const sessionContext = useContext(SessionContext);

    const { data: orderDetail, isLoading, error } = useGetOrderDetail(id || '');
    useOrderCollaboration(id);
    const archiveOrderMutation = useArchiveOrder();
    const completeOrderMutation = useCompleteOrder();
    const updateOrderDetailsMutation = useUpdateOrderDetails();
    const { trackView } = useRecentActivity();
    const hasTrackedView = useRef(false);
    const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumbs();
    const { settings: tableSettings, updateSettings: updateTableSettings, tableSettingsSx } = useTableSettings('order');
    const [isManualRefreshing, setIsManualRefreshing] = useState(false);
    const seenOrderNotificationIds = useRef<Set<string>>(new Set());
    const isInitialOrderNotifications = useRef(true);

    // Notifications this user received for the order being viewed — powers the
    // sidebar "Notifications" section preview and the order notifications drawer.
    const { data: orderNotificationsData } = useGetNotifications(!!id, undefined, 0, undefined, id || undefined);
    const orderNotifications = orderNotificationsData?.notifications ?? [];

    const refreshOrder = useCallback(async () => {
        if (!id) return;
        await invalidateOrder(queryClient, id);
    }, [id, queryClient]);

    const handleManualRefresh = useCallback(async () => {
        if (!id) return;
        setIsManualRefreshing(true);
        try {
            await invalidateOrder(queryClient, id);
        } finally {
            setIsManualRefreshing(false);
        }
    }, [id, queryClient]);

    // When a new notification arrives for this order (rejection, confirmation, etc.),
    // refresh the order query family so tabs stay consistent with activity/notifications.
    useEffect(() => {
        seenOrderNotificationIds.current = new Set();
        isInitialOrderNotifications.current = true;
    }, [id]);

    useEffect(() => {
        if (!id || !orderNotificationsData?.notifications) return;

        if (isInitialOrderNotifications.current) {
            orderNotificationsData.notifications.forEach((notification) => {
                seenOrderNotificationIds.current.add(notification._id);
            });
            isInitialOrderNotifications.current = false;
            return;
        }

        let hasNewNotification = false;
        for (const notification of orderNotificationsData.notifications) {
            if (!seenOrderNotificationIds.current.has(notification._id)) {
                hasNewNotification = true;
                seenOrderNotificationIds.current.add(notification._id);
            }
        }

        if (hasNewNotification) {
            invalidateOrder(queryClient, id);
        }
    }, [id, orderNotificationsData, queryClient]);

    // The originating list writes its session cache from state and unmounts as this
    // page mounts, so the cache at mount time is the list exactly as the row was
    // clicked. Snapshot it once: the breadcrumb must keep pointing at that view even
    // if the cache is rewritten later (for example by the order drawer).
    const [listViewStateAtOpen] = useState(() => {
        const readListViewState = (cacheKey: string): unknown => {
            try {
                const cached = sessionStorage.getItem(cacheKey);
                return cached ? JSON.parse(cached) : undefined;
            } catch {
                return undefined;
            }
        };
        return {
            dashboard: readListViewState('dashboard_view_state_cache'),
            home: readListViewState('home_view_state_cache'),
        };
    });

    // Set breadcrumbs in the top bar
    const getBreadcrumbItems = useCallback(() => {
        const locationState = location.state as {
            source?: Tab;
            homeReturnState?: unknown;
        } | null;
        const source = locationState?.source;
        const activeTab = source || (localStorage.getItem('a_st') as Tab | null);

        // Hand the snapshot back on the breadcrumb (search included) only when this
        // detail was reached from that list; a refresh or sidebar click never
        // restores search.
        if (activeTab === 'dashboard') {
            const dashboardViewState = source === 'dashboard'
                ? listViewStateAtOpen.dashboard
                : undefined;
            return [{
                label: 'Orders',
                path: '/dashboard',
                ...(dashboardViewState ? { state: { dashboardViewState } } : {}),
            }];
        } else if (activeTab === 'todo') {
            return [{ label: 'Main', path: '/todo' }, { label: 'To-Do', path: '/todo' }];
        } else if (activeTab === 'activity') {
            return [{ label: 'Main', path: '/' }, { label: 'Activity', path: '/' }];
        } else if (activeTab === 'flagged') {
            return [{ label: 'Flagged Parts', path: '/flagged' }];
        } else if (activeTab === 'invoices') {
            return [{ label: 'Parts Invoicing', path: '/invoices' }];
        } else if (activeTab === 'delivery-board') {
            return [{ label: 'Delivery Board', path: '/delivery-board' }];
        } else if (activeTab === 'inventory') {
            return [{ label: 'Inventory', path: '/inventory/purchase-orders' }];
        } else if (activeTab === 'home') {
            const homeViewState = source === 'home'
                ? listViewStateAtOpen.home
                : undefined;
            const homeState = {
                ...(homeViewState ? { homeViewState } : {}),
                ...(locationState?.homeReturnState ? { homeReturnState: locationState.homeReturnState } : {}),
            };
            return [{
                label: 'Home',
                path: '/home',
                ...(Object.keys(homeState).length > 0 ? { state: homeState } : {}),
            }];
        } else {
            return [{ label: 'Home', path: '/home' }];
        }
    }, [listViewStateAtOpen, location.state]);

    useEffect(() => {
        const items = getBreadcrumbItems();
        const currentPage = orderDetail
            ? (orderDetail.roNumber || '—')
            : undefined;
        // Browser tab/history uses the bare number ("164746 | <org>").
        const documentTitle = orderDetail?.roNumber ? String(orderDetail.roNumber) : undefined;
        setBreadcrumbs(items, currentPage, documentTitle);

        return () => clearBreadcrumbs();
    }, [orderDetail, getBreadcrumbItems, setBreadcrumbs, clearBreadcrumbs]);

    // Track recent view with proper label when data loads
    useEffect(() => {
        if (orderDetail && id && !hasTrackedView.current) {
            const displayLabel = orderDetail.roNumber
                ? `Order • RO ${orderDetail.roNumber}`
                : orderDetail.vin
                    ? `Order • ${orderDetail.vin}`
                    : `Order • ${id}`;
            trackView({
                type: 'order',
                key: `order:${id}`,
                label: displayLabel,
                path: `/order/${id}`,
                orderId: id,
                meta: { vin: orderDetail.vin, roNumber: orderDetail.roNumber }
            });
            hasTrackedView.current = true;
        }
    }, [orderDetail, id, trackView]);

    // Open the chat drawer only when the user explicitly arrived with intent
    // (e.g. clicking into an order from the global Messages drawer). Auto-
    // opening on the mere presence of messages was removed by request.
    const hasOpenedChat = useRef(false);
    useEffect(() => {
        if (orderDetail && !hasOpenedChat.current) {
            if (location.state?.openChat === true) {
                setActiveDrawer('chat');
            }
            hasOpenedChat.current = true;
        }
    }, [orderDetail, location.state]);

    // Reset hasSetInitialStatus when order ID changes (navigating to different order)
    useEffect(() => {
        hasSetInitialStatus.current = false;
        hasTrackedView.current = false;
        hasOpenedChat.current = false;
    }, [id]);

    /**
     * Toggle body classes consumed by global CSS in `index.css` to push
     * Chakra modals and their overlays into the LHS area while a side
     * drawer (chat / supplier comms / activity log) is open. This lets users
     * keep the drawer open while interacting with modals (e.g. PartOptionModal).
     */
    useEffect(() => {
        const commsOpen = isChatOpen || isSupplierNotesOpen || isCarlDrawerOpen;
        document.body.classList.toggle('partbay-comms-drawer-open', commsOpen);
        document.body.classList.toggle('partbay-activity-drawer-open', isActivityLogOpen || isOrderNotificationsOpen || isEmsHistoryOpen);
        return () => {
            document.body.classList.remove('partbay-comms-drawer-open');
            document.body.classList.remove('partbay-activity-drawer-open');
        };
    }, [isChatOpen, isSupplierNotesOpen, isActivityLogOpen, isOrderNotificationsOpen, isEmsHistoryOpen, isCarlDrawerOpen]);

    // Side drawers are exclusive: opening any platform drawer closes Carl, and vice versa.
    useEffect(() => {
        if (activeDrawer) setIsCarlDrawerOpen(false);
    }, [activeDrawer]);

    // Handle initialStatus from navigation state (e.g., from notifications, action badges)
    useEffect(() => {
        if (initialStatus) {
            setSelectedStatus(initialStatus);
        }
    }, [initialStatus, id]);

    useEffect(() => {
        if (orderDetail && !hasSetInitialStatus.current) {
            let defaultStatus: OrderStep = 'newParts';

            if (initialStatus) {
                defaultStatus = initialStatus;
            } else {
                if (orderDetail.newParts.parts.length > 0) {
                    defaultStatus = 'newParts';
                } else if (orderDetail.selection.length > 0) {
                    defaultStatus = 'selection';
                } else if (orderDetail.ordered.length > 0) {
                    defaultStatus = 'ordered';
                } else if (orderDetail.quoting.length > 0) {
                    defaultStatus = 'quoting';
                }
            }

            setSelectedStatus(defaultStatus);
            setRONumberValue(orderDetail.roNumber || '');
            hasSetInitialStatus.current = true;
        }
    }, [orderDetail, initialStatus]);

    const shouldShowLocationWarning = useMemo(() => {
        if (!orderDetail?.locationId || !sessionContext?.isAuthenticated) return true;

        const userLocation = sessionContext.locationId;
        return userLocation !== 'all' && !userLocation.includes(orderDetail.locationId);
    }, [sessionContext, orderDetail?.locationId]);

    const locationMismatchTooltip = useMemo(() => {
        if (!shouldShowLocationWarning || !orderDetail || !sessionContext?.locationName) {
            return undefined;
        }
        return `Can't remove tag.\nOrder Location: '${orderDetail.locationName}'\nYour Location: '${sessionContext.locationName}'`;
    }, [shouldShowLocationWarning, orderDetail, sessionContext?.locationName]);

    const supplierOrderSummaries = useMemo<Array<SupplierOrderSummary>>(() => {
        if (!orderDetail) return [];

        const dedupedOrders = new Map<string, SupplierOrderSummary>();
        const allOrderedSections = orderDetail.ordered.flatMap((tab) => tab.sections);

        allOrderedSections.forEach((section) => {
            if (section.source !== 'partstrader' && section.source !== 'opstrax' && section.source !== 'ccc') return;
            if (dedupedOrders.has(section.key)) return;

            dedupedOrders.set(section.key, {
                supplierOrderId: section.key,
                supplierName: section.title,
                status: section.status,
                phoneNumber: section.phoneNumber,
                email: section.email,
                source: section.source,
                displayId: section.displayId,
            });
        });

        return Array.from(dedupedOrders.values());
    }, [orderDetail]);

    const supplierCommunicationCount = useMemo(() => {
        const notesCount = orderDetail?.supplierNotes.length ?? 0;
        const messageCounts = orderDetail?.supplierMessageCounts ?? {};
        const totalMessages = Object.values(messageCounts).reduce((sum, count) => sum + count, 0);
        return notesCount + totalMessages;
    }, [orderDetail?.supplierNotes, orderDetail?.supplierMessageCounts]);

    const openSupplierCommunications = (options?: { initialTab?: 'quoting-comments' | 'order-messages'; supplierOrderId?: string; }) => {
        setSupplierNotesInitialTab(options?.initialTab ?? 'quoting-comments');
        setSupplierNotesInitialSupplierOrderId(options?.supplierOrderId ?? null);
        setActiveDrawer('supplierNotes');
    };

    const { copy: copyText, isCopied } = useCopyToClipboard();

    const copyToClipboard = async (text: string, key: string, label: string = 'VIN', e?: React.MouseEvent) => {
        const ok = await copyText(text, key, e ? { x: e.clientX, y: e.clientY } : null);
        if (!ok) {
            toast({
                title: 'Copy Failed',
                description: `Failed to copy ${label} to clipboard`,
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    const exportOrderSummary = async (supplierOrderIds: string[] | 'all') => {
        if (!orderDetail || !id) return;
        const preview = window.open('', '_blank');
        if (preview) {
            preview.document.title = 'Preparing purchase order summary';
            preview.document.body.innerHTML = '<p style="font-family: sans-serif; padding: 24px">Preparing purchase order summary…</p>';
        }
        setIsExportingSummary(true);
        try {
            const file = await exportSummary(id, supplierOrderIds, orderDetail.ordered, {
                roNumber: orderDetail.roNumber,
                claimNumber: orderDetail.claimNumber,
                insuranceName: orderDetail.insurance,
                vin: orderDetail.vin,
                vehicle: orderDetail.vehicleName,
                locationName: orderDetail.locationName,
            }, orderDetail.selection);
            const opened = saveAndPrintPdf(file, preview);
            toast({
                title: 'Purchase order summary saved',
                description: opened
                    ? 'The PDF downloaded. Use the print dialog if you want a paper copy.'
                    : 'The PDF downloaded. Open it to print a copy.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
        } catch (error) {
            preview?.close();
            toast({
                title: 'Export failed',
                description: exportErrorMessage(error),
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsExportingSummary(false);
        }
    };

    const toggleSummaryOrder = (supplierOrderId: string) => {
        setSummarySelection((current) => (
            current.includes(supplierOrderId)
                ? current.filter((id) => id !== supplierOrderId)
                : current.concat(supplierOrderId)
        ));
    };

    const handleArchiveOrder = async () => {
        if (!orderDetail || !id) return;
        if (!sessionContext?.permissions?.includes('archive-unarchive')) return;

        if (shouldShowLocationWarning) return;

        try {
            await archiveOrderMutation.mutateAsync({ orderId: id });
            toast({
                title: !orderDetail.archived ? 'Order Archived' : 'Order Unarchived',
                description: !orderDetail.archived ? 'The order has been archived successfully' : 'The order has been unarchived',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: !orderDetail.archived ? 'Failed to archive order' : 'Failed to unarchive order',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const handleCompleteOrder = async () => {
        if (!orderDetail || !id) return;
        if (!sessionContext?.permissions?.includes('complete-uncomplete')) return;

        if (shouldShowLocationWarning) return;

        try {
            await completeOrderMutation.mutateAsync({ orderId: id });
            toast({
                title: !orderDetail.completed ? 'Order Completed' : 'Order Reopened',
                description: !orderDetail.completed ? 'The order has been marked as completed' : 'The order has been reopened',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Error',
                description: !orderDetail.completed ? 'Failed to complete order' : 'Failed to reopen order',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const formatDate = (date: Date) => {
        return new Date(date).toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: '2-digit'
        });
    };

    const formatTime = (date: Date) => {
        return new Date(date).toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short'
        });
    };

    const handleStartEditingRONumber = () => setIsEditingRONumber(true);

    const handleSaveRONumber = async () => {
        if (!id || roNumberValue === orderDetail?.roNumber) {
            setIsEditingRONumber(false);
            return;
        }
        try {
            await updateOrderDetailsMutation.mutateAsync({ orderId: id, roNumber: roNumberValue });
        } catch {
        } finally {
            setIsEditingRONumber(false);
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            if (isEditingRONumber) handleSaveRONumber();
        } else if (e.key === 'Escape') {
            if (isEditingRONumber) {
                setRONumberValue(orderDetail?.roNumber || '');
                setIsEditingRONumber(false);
            }
        }
    };

    const removeTagDirectly = (tag: 'potential-total' | 'quotes-only' | 'manually-flagged') => {
        if (!id) return;
        if (tag === 'potential-total') {
            updateOrderDetailsMutation.mutate({ orderId: id, potentialTotal: false });
        } else if (tag === 'quotes-only') {
            updateOrderDetailsMutation.mutate({ orderId: id, quotesOnly: false });
        } else if (tag === 'manually-flagged') {
            updateOrderDetailsMutation.mutate({ orderId: id, manuallyFlagged: false });
        }
    };

    /* ── Loading / error states ── */
    if (isLoading) {
        return (
            <Center h="calc(100dvh - var(--partbay-topbar))">
                <Spinner size="xl" color="blue.500" />
            </Center>
        );
    }

    if (error || !orderDetail) {
        return (
            <Box w="100%" maxW="1400px">
                <Alert status="error">
                    <AlertIcon />
                    Failed to load order details
                </Alert>
            </Box>
        );
    }

    /* ── Tab pills config ── */
    const tabs: { key: OrderStep; label: string; isDisabled?: boolean; icon: React.ElementType; }[] = [
        { key: 'newParts', label: 'New Parts', icon: FiFileText as React.ElementType },
        { key: 'quoting', label: 'Quoting', isDisabled: orderDetail.quoting.length === 0, icon: FiDollarSign as React.ElementType },
        { key: 'selection', label: 'Ready to Order', isDisabled: orderDetail.selection.length === 0, icon: FiShoppingCart as React.ElementType },
        { key: 'ordered', label: 'Ordered', isDisabled: orderDetail.ordered.length === 0, icon: FiPackage as React.ElementType },
        ...(sessionContext?.isAuthenticated && sessionContext.rekeyingEnabled
            ? [{ key: 'rekey' as OrderStep, label: 'Re-Keying', isDisabled: !orderDetail.rekey, icon: FiRefreshCw as React.ElementType }]
            : [])
    ];

    const showAskCarl = canUseAskCarl(sessionContext);

    /* ── Whether any tags are present (to conditionally render the row) ── */
    const hasCurrentUserReviewed = sessionContext?.isAuthenticated
        ? orderDetail.reviewedByUsers.some((reviewer) => reviewer._id === sessionContext.userId)
        : false;

    const hasAnyTag =
        orderDetail.archived ||
        orderDetail.completed ||
        orderDetail.potentialTotal ||
        orderDetail.oemCertification ||
        orderDetail.quotesOnly ||
        orderDetail.oemEndorsement ||
        !!orderDetail.dealerReferral ||
        orderDetail.teslaOrderError ||
        orderDetail.manuallyFlagged ||
        orderDetail.reviewedByUsers.length > 0;

    /* ── Delivery label depends on active tab ── */
    const deliveryLabel = selectedStatus === 'ordered' ? 'Delivery ETA' : 'Delivery Time';
    const orderedEtaRange = getEtaRange(
        orderDetail.ordered.flatMap((tab) =>
            tab.sections.flatMap((section) =>
                section.parts
                    .map((part) => part.correctedDelivery || part.delivery)
                    .filter((eta): eta is string => Boolean(eta))
            )
        )
    );

    const deliveryValue = selectedStatus === 'ordered'
        ? (orderedEtaRange || orderDetail.deliveryETA || 'N/A')
        : (orderDetail.deliveryTime || 'N/A');

    return (
        <>
            {/* ════════════════════════════════════════════════════════
                TOP-LEVEL: full-width, full-height, two-column flex
            ════════════════════════════════════════════════════════ */}
            <Box
                className="partbay-order-detail-wrapper"
                w="calc(100% + 2rem)"
                mx={-4}
                mt={-4}
                mb={-4}
                h="calc(100dvh - var(--partbay-topbar))"
                display="flex"
                overflow="hidden"
                bg="white"
                position="relative"
            >
                {/* ── LEFT COLUMN ─────────────────────────────────── */}
                <Box flex={1} display="flex" flexDirection="column" overflow="hidden" minW={0}>

                    {/* HEADER */}
                    <Flex
                        px={6}
                        pt={4}
                        pb={1}
                        align="flex-start"
                        justify="space-between"
                        flexShrink={0}
                    >
                        {/* Left: vehicle name + RO on one line, VIN · Shop below */}
                        <VStack spacing={1.5} minW={0} mr={4} align="flex-start">
                            <HStack spacing={2} minW={0} align="center">
                                <Heading size="md" color="gray.900" lineHeight="1.25" noOfLines={1} flexShrink={0}>
                                    {orderDetail.vehicleName}
                                </Heading>
                                <Heading size="md" color="gray.400" lineHeight="1.25" flexShrink={0}>·</Heading>
                                {isEditingRONumber ? (
                                    <Input
                                        value={roNumberValue}
                                        onChange={(e) => setRONumberValue(e.target.value)}
                                        onBlur={handleSaveRONumber}
                                        onKeyDown={handleKeyPress}
                                        size="sm"
                                        width="120px"
                                        autoFocus
                                        isDisabled={updateOrderDetailsMutation.isPending}
                                        fontSize="sm"
                                        color="gray.900"
                                    />
                                ) : (
                                    <>
                                        <Heading size="md" color="gray.900" lineHeight="1.25" flexShrink={0}>{orderDetail.roNumber || '—'}</Heading>
                                        <IconButton
                                            aria-label="Edit RO Number"
                                            icon={<Icon as={TbPencil as React.ElementType} w={3} h={3} />}
                                            size="xs"
                                            variant="ghost"
                                            onClick={handleStartEditingRONumber}
                                            isDisabled={updateOrderDetailsMutation.isPending}
                                            _hover={{ bg: 'gray.100' }}
                                            minW="auto"
                                            h="auto"
                                            p={1}
                                        />
                                    </>
                                )}
                                <EnterEstimateBadge canEnter={orderDetail.canEnterEstimate} boxSize="20px" showWhenCanEnter />
                                {/* Order tags inline, right after RO */}
                                {hasAnyTag && (
                                    <>
                                        {orderDetail.archived && (
                                            <OrderTag
                                                tag="archived"
                                                text={archiveOrderMutation.isPending ? 'Unarchiving...' : 'ARCHIVED'}
                                                onClick={handleArchiveOrder}
                                                isPending={archiveOrderMutation.isPending}
                                                tooltip={locationMismatchTooltip}
                                                isDisabled={shouldShowLocationWarning}
                                            />
                                        )}
                                        {orderDetail.completed && (
                                            <OrderTag
                                                tag="completed"
                                                text={completeOrderMutation.isPending ? 'Updating...' : 'COMPLETED'}
                                                onClick={handleCompleteOrder}
                                                isPending={completeOrderMutation.isPending}
                                                tooltip={locationMismatchTooltip}
                                                isDisabled={shouldShowLocationWarning}
                                            />
                                        )}
                                        {orderDetail.potentialTotal && (
                                            <OrderTag
                                                tag="potential-total"
                                                text="Potential Total"
                                                onClick={() => removeTagDirectly('potential-total')}
                                                tooltip={locationMismatchTooltip}
                                                isDisabled={shouldShowLocationWarning}
                                            />
                                        )}
                                        {orderDetail.oemCertification && (
                                            <OrderTag tag="oem-cetification" text="OEM Certification" />
                                        )}
                                        {orderDetail.quotesOnly && (
                                            <OrderTag
                                                tag="quotes-only"
                                                text="Quotes Only"
                                                onClick={() => removeTagDirectly('quotes-only')}
                                                tooltip={locationMismatchTooltip}
                                                isDisabled={shouldShowLocationWarning}
                                            />
                                        )}
                                        {orderDetail.oemEndorsement && (
                                            <OrderTag tag="oem-endorsement" text="OEM Endorsement" />
                                        )}
                                        {orderDetail.dealerReferral && (
                                            <OrderTag tag="dealer-referral" text={orderDetail.dealerReferral} />
                                        )}
                                        {orderDetail.reviewedByUsers.map((reviewer) => (
                                            <OrderTag
                                                key={reviewer._id}
                                                tag="reviewed-by"
                                                text={`Reviewed By ${reviewer.displayName}`}
                                                onClick={
                                                    sessionContext?.isAuthenticated &&
                                                        reviewer._id === sessionContext.userId &&
                                                        id
                                                        ? () => updateOrderDetailsMutation.mutate({ orderId: id, reviewedByMe: false })
                                                        : undefined
                                                }
                                            />
                                        ))}
                                        {orderDetail.teslaOrderError && (
                                            <Tooltip label="Verify RO exists in Tesla EPC" placement="top">
                                                <Box display="inline-flex">
                                                    <Icon as={TbAlertTriangle as React.ElementType} w={5} h={5} color="red.500" />
                                                </Box>
                                            </Tooltip>
                                        )}
                                        {orderDetail.manuallyFlagged && (
                                            <OrderTag
                                                tag="manually-flagged"
                                                text="Save for Later"
                                                onClick={() => removeTagDirectly('manually-flagged')}
                                                tooltip={locationMismatchTooltip}
                                                isDisabled={shouldShowLocationWarning}
                                            />
                                        )}
                                    </>
                                )}
                            </HStack>

                            {/* Subheader: VIN · Shop */}
                            <HStack spacing={1.5} align="center">
                                <HStack spacing={1} align="center">
                                    <Text fontSize="sm" color="gray.500" lineHeight="1.4">VIN:</Text>
                                    <CopyTooltip isCopied={isCopied('vin-full')}>
                                        <Text as="span" fontSize="sm" color="gray.800" lineHeight="1.4">
                                            {orderDetail.vin.slice(0, -8)}
                                            <CopyTooltip isCopied={isCopied('vin-last8')}>
                                                <Text
                                                    as="span"
                                                    fontWeight="bold"
                                                    textDecoration="underline"
                                                    cursor="pointer"
                                                    _hover={{ color: 'blue.600' }}
                                                    onClick={(e) => copyToClipboard(orderDetail.vin.slice(-8), 'vin-last8', 'Last 8 of VIN', e)}
                                                >
                                                    {orderDetail.vin.slice(-8)}
                                                </Text>
                                            </CopyTooltip>
                                        </Text>
                                    </CopyTooltip>
                                    <IconButton
                                        aria-label="Copy full VIN"
                                        icon={<Icon as={TbCopy as React.ElementType} w={3} h={3} />}
                                        size="xs"
                                        variant="ghost"
                                        onClick={(e) => copyToClipboard(orderDetail.vin, 'vin-full', 'Full VIN', e)}
                                        p={0.5}
                                        minW="auto"
                                        h="auto"
                                        color="gray.400"
                                        _hover={{ color: 'gray.600' }}
                                        flexShrink={0}
                                    />
                                </HStack>
                                <Text fontSize="sm" color="gray.300" lineHeight="1">·</Text>
                                <Text fontSize="sm" color="gray.500">Location: <Text as="span" color="gray.800">{orderDetail.locationName}</Text></Text>
                                <Text fontSize="sm" color="gray.300" lineHeight="1">·</Text>
                                <HStack spacing={1} align="center">
                                    <Text fontSize="sm" color="gray.500">Insurance:</Text>
                                    <Text fontSize="sm" color="gray.800">{orderDetail.insurance || '—'}</Text>
                                    {orderDetail.drpJob && (
                                        <Badge colorScheme="blue" variant="subtle" fontSize="10px">DRP</Badge>
                                    )}
                                </HStack>
                            </HStack>
                        </VStack>

                        {/* Right: action icons */}
                        <HStack spacing={3} flexShrink={0} align="center">
                            {id && <OrderCollaborationAvatars orderId={id} />}
                            {/* Ask Carl — opens the Carl drawer (admins only) */}
                            {showAskCarl && (
                                <Tooltip label="Ask Carl" placement="top">
                                    <Flex
                                        cursor="pointer"
                                        onClick={() => {
                                            setActiveDrawer(null);
                                            setIsCarlDrawerOpen(true);
                                        }}
                                        align="center"
                                        justify="center"
                                        w={7}
                                        h={7}
                                    >
                                        <Icon as={TbSparkles as React.ElementType} w={6} h={6} color="brand.solid" />
                                    </Flex>
                                </Tooltip>
                            )}
                            {/* Supplier communications */}
                            <Tooltip label="Supplier Communications" placement="top">
                                <Flex
                                    position="relative"
                                    cursor="pointer"
                                    onClick={() => openSupplierCommunications()}
                                    align="center"
                                    justify="center"
                                    w={7}
                                    h={7}
                                >
                                    <Icon as={TbTruck as React.ElementType} w={6} h={6} color="orange.500" />
                                    {supplierCommunicationCount > 0 && (
                                        <Flex
                                            position="absolute"
                                            top="-2px"
                                            right="-4px"
                                            bg="orange.500"
                                            color="white"
                                            borderRadius="full"
                                            w="16px"
                                            h="16px"
                                            align="center"
                                            justify="center"
                                            fontSize="9px"
                                            fontWeight="bold"
                                            lineHeight="1"
                                        >
                                            {supplierCommunicationCount}
                                        </Flex>
                                    )}
                                </Flex>
                            </Tooltip>

                            {/* Chat / messaging */}
                            <Tooltip label="Order Chat" placement="top">
                                <Flex
                                    position="relative"
                                    cursor="pointer"
                                    onClick={() => { setChatComposeMode('message'); setActiveDrawer('chat'); }}
                                    align="center"
                                    justify="center"
                                    w={7}
                                    h={7}
                                >
                                    <Icon as={TbMessageCircle as React.ElementType} w={6} h={6} color="gray.500" />
                                    {(orderDetail.notes?.length ?? 0) > 0 && (
                                        <Flex
                                            position="absolute"
                                            top="-2px"
                                            right="-4px"
                                            bg="brand.solid"
                                            color="white"
                                            borderRadius="full"
                                            w="16px"
                                            h="16px"
                                            align="center"
                                            justify="center"
                                            fontSize="9px"
                                            fontWeight="bold"
                                            lineHeight="1"
                                        >
                                            {orderDetail.notes.length}
                                        </Flex>
                                    )}
                                </Flex>
                            </Tooltip>

                            {/* Options button — matches dashboard table style */}
                            {((sessionContext?.permissions?.includes('archive-unarchive') && !orderDetail.archived) || supplierOrdersOnOrder(orderDetail.ordered).length > 0) && (
                                <Menu onClose={() => { setExportPanel('menu'); setSummarySelection([]); }}>
                                    <MenuButton
                                        as={IconButton}
                                        aria-label="Options"
                                        icon={<Icon as={HiOutlineDotsVertical as React.ElementType} w={5} h={5} />}
                                        size="sm"
                                        variant="ghost"
                                        colorScheme="gray"
                                        borderRadius="lg"
                                        _hover={{ bg: 'gray.50' }}
                                        _active={{ bg: 'gray.100' }}
                                        _focus={{ boxShadow: 'outline' }}
                                    />
                                    <MenuList py={1} minW={exportPanel === 'pick' ? '280px' : '220px'}>
                                        {exportPanel === 'menu' && sessionContext?.permissions?.includes('archive-unarchive') && !orderDetail.archived && (
                                        <>
                                        {sessionContext?.permissions?.includes('complete-uncomplete') && !orderDetail.completed && (
                                            <MenuItem
                                                onClick={handleCompleteOrder}
                                                fontSize="sm"
                                                px={3}
                                                py={2}
                                                color="gray.700"
                                                _hover={{ bg: 'brand.subtle', color: 'brand.solid' }}
                                                isDisabled={completeOrderMutation.isPending}
                                            >
                                                <HStack spacing={2}>
                                                    <Icon as={ORDER_ACTION_ICONS.complete as React.ElementType} boxSize={4} color="gray.500" />
                                                    <Text>Mark Complete</Text>
                                                </HStack>
                                            </MenuItem>
                                        )}
                                        <MenuItem
                                            onClick={handleArchiveOrder}
                                            fontSize="sm"
                                            px={3}
                                            py={2}
                                            color="gray.700"
                                            _hover={{ bg: 'brand.subtle', color: 'brand.solid' }}
                                            isDisabled={archiveOrderMutation.isPending}
                                        >
                                            <HStack spacing={2}>
                                                <Icon as={ORDER_ACTION_ICONS.archive as React.ElementType} boxSize={4} color="gray.500" />
                                                <Text>Archive Order</Text>
                                            </HStack>
                                        </MenuItem>
                                        {sessionContext?.permissions?.includes('update-order-details') && !orderDetail.quotesOnly && (
                                            <MenuItem
                                                onClick={() => {
                                                    if (!id) return;
                                                    updateOrderDetailsMutation.mutate({ orderId: id, quotesOnly: true });
                                                }}
                                                fontSize="sm"
                                                px={3}
                                                py={2}
                                                color="gray.700"
                                                _hover={{ bg: 'brand.subtle', color: 'brand.solid' }}
                                                isDisabled={updateOrderDetailsMutation.isPending}
                                            >
                                                <HStack spacing={2}>
                                                    <Icon as={FiFileText as React.ElementType} boxSize={4} color="gray.500" />
                                                    <Text>Mark "Quote Only"</Text>
                                                </HStack>
                                            </MenuItem>
                                        )}
                                        {sessionContext?.permissions?.includes('update-order-details') && !orderDetail.potentialTotal && (
                                            <MenuItem
                                                onClick={() => {
                                                    if (!id) return;
                                                    updateOrderDetailsMutation.mutate({ orderId: id, potentialTotal: true });
                                                }}
                                                fontSize="sm"
                                                px={3}
                                                py={2}
                                                color="gray.700"
                                                _hover={{ bg: 'brand.subtle', color: 'brand.solid' }}
                                                isDisabled={updateOrderDetailsMutation.isPending}
                                            >
                                                <HStack spacing={2}>
                                                    <Icon as={FaExclamationTriangle as React.ElementType} boxSize={4} color="gray.500" />
                                                    <Text>Mark "Potential Total"</Text>
                                                </HStack>
                                            </MenuItem>
                                        )}
                                        {sessionContext?.permissions?.includes('flag-order') && !orderDetail.manuallyFlagged && (
                                            <MenuItem
                                                onClick={() => {
                                                    if (!id) return;
                                                    updateOrderDetailsMutation.mutate({ orderId: id, manuallyFlagged: true });
                                                }}
                                                fontSize="sm"
                                                px={3}
                                                py={2}
                                                color="gray.700"
                                                _hover={{ bg: 'brand.subtle', color: 'brand.solid' }}
                                                isDisabled={updateOrderDetailsMutation.isPending}
                                            >
                                                <HStack spacing={2}>
                                                    <Icon as={ORDER_ACTION_ICONS.flag as React.ElementType} boxSize={4} color="gray.500" />
                                                    <Text>Save for Later</Text>
                                                </HStack>
                                            </MenuItem>
                                        )}
                                        {sessionContext?.permissions?.includes('flag-order') && (
                                            <MenuItem
                                                onClick={() => {
                                                    if (!id) return;
                                                    updateOrderDetailsMutation.mutate({
                                                        orderId: id,
                                                        reviewedByMe: !hasCurrentUserReviewed
                                                    });
                                                }}
                                                fontSize="sm"
                                                px={3}
                                                py={2}
                                                color="gray.700"
                                                _hover={{ bg: 'brand.subtle', color: 'brand.solid' }}
                                                isDisabled={updateOrderDetailsMutation.isPending || !sessionContext?.isAuthenticated}
                                            >
                                                <HStack spacing={2}>
                                                    <Icon
                                                        as={(hasCurrentUserReviewed
                                                            ? ORDER_ACTION_ICONS.removeReview
                                                            : ORDER_ACTION_ICONS.markReviewed) as React.ElementType}
                                                        boxSize={4}
                                                        color="gray.500"
                                                    />
                                                    <Text>{hasCurrentUserReviewed ? 'Remove My Review' : 'Mark Reviewed'}</Text>
                                                </HStack>
                                            </MenuItem>
                                        )}
                                        </>
                                        )}
                                        {exportPanel === 'menu' && supplierOrdersOnOrder(orderDetail.ordered).length > 0 && (
                                            <MenuItemRow
                                                kind="drillIn"
                                                closeOnSelect={false}
                                                onClick={() => setExportPanel('scope')}
                                            >
                                                Export purchase orders
                                            </MenuItemRow>
                                        )}
                                        {exportPanel === 'scope' && (
                                            <>
                                                <MenuItemRow kind="single" closeOnSelect={false} onClick={() => setExportPanel('menu')}>
                                                    <HStack spacing={2}>
                                                        <YaxleIcon name="arrowLeft" size="sm" />
                                                        <Text>Back</Text>
                                                    </HStack>
                                                </MenuItemRow>
                                                <MenuItemRow kind="single" onClick={() => { void exportOrderSummary('all'); }}>
                                                    All purchase orders
                                                </MenuItemRow>
                                                <MenuItemRow
                                                    kind="drillIn"
                                                    closeOnSelect={false}
                                                    onClick={() => setExportPanel('pick')}
                                                >
                                                    Choose purchase orders
                                                </MenuItemRow>
                                            </>
                                        )}
                                        {exportPanel === 'pick' && (
                                            <>
                                                <MenuItemRow kind="single" closeOnSelect={false} onClick={() => setExportPanel('scope')}>
                                                    <HStack spacing={2}>
                                                        <YaxleIcon name="arrowLeft" size="sm" />
                                                        <Text>Back</Text>
                                                    </HStack>
                                                </MenuItemRow>
                                                <Box px={1} maxH="280px" overflowY="auto">
                                                    {supplierOrdersOnOrder(orderDetail.ordered).map((supplierOrder) => (
                                                        <CheckboxMenuRow
                                                            key={supplierOrder.id}
                                                            isSelected={summarySelection.includes(supplierOrder.id)}
                                                            onClick={() => toggleSummaryOrder(supplierOrder.id)}
                                                        >
                                                            <VStack align="flex-start" spacing={0}>
                                                                <Text>{supplierOrder.title}</Text>
                                                                <Text fontSize="xs" color="fg.muted">
                                                                    {[supplierOrder.displayId, supplierOrder.status, `${supplierOrder.partCount} ${supplierOrder.partCount === 1 ? 'part' : 'parts'}`].filter(Boolean).join(' · ')}
                                                                </Text>
                                                            </VStack>
                                                        </CheckboxMenuRow>
                                                    ))}
                                                </Box>
                                                <MenuItemRow
                                                    kind="single"
                                                    isDisabled={summarySelection.length === 0 || isExportingSummary}
                                                    onClick={() => { void exportOrderSummary(summarySelection); }}
                                                >
                                                    {summarySelection.length === 1 ? 'Export 1 purchase order' : `Export ${summarySelection.length} purchase orders`}
                                                </MenuItemRow>
                                            </>
                                        )}
                                    </MenuList>
                                </Menu>
                            )}
                        </HStack>
                    </Flex>

                    {/* TAB SELECTOR – numberline stepper: phase pills joined by connector lines that fill as the order progresses */}
                    <Flex px={6} py={3} flexShrink={0} borderBottom="1px solid" borderColor="gray.200" align="center" justify="space-between">
                        <Flex align="center" flex={1} mr={4}>
                            {(() => {
                                const activeIdx = tabs.findIndex(t => t.key === selectedStatus);
                                const prevIdx = tabs.findIndex(t => t.key === prevSelectedStatusRef.current);
                                const fromIdx = prevIdx === -1 ? activeIdx : prevIdx;
                                return tabs.flatMap(({ key, label, isDisabled, icon }, idx) => {
                                    const isActive = selectedStatus === key;
                                    const disabled = isDisabled ?? false;
                                    const elements = [
                                        <Button
                                            key={key}
                                            size="sm"
                                            h="32px"
                                            px={4}
                                            borderRadius="full"
                                            fontWeight="medium"
                                            fontSize="sm"
                                            bg={isActive ? 'brand.solid' : 'bg.subtle'}
                                            color={isActive ? 'fg.onBrand' : 'fg.muted'}
                                            _hover={{
                                                bg: isActive ? 'brand.solid' : 'bg.muted',
                                            }}
                                            _active={{
                                                bg: isActive ? 'brand.solid' : 'bg.muted',
                                            }}
                                            opacity={disabled ? 0.45 : 1}
                                            cursor={disabled ? 'not-allowed' : 'pointer'}
                                            onClick={() => !disabled && setSelectedStatus(key)}
                                            leftIcon={<Icon as={icon} boxSize={3.5} />}
                                            flexShrink={0}
                                            transition="all 0.4s ease"
                                        >
                                            {label}
                                        </Button>
                                    ];
                                    if (idx < tabs.length - 1) {
                                        elements.push(
                                            <Box
                                                key={`${key}-line`}
                                                flex={1}
                                                h="2px"
                                                mx={2}
                                                borderRadius="full"
                                                position="relative"
                                                bg="border.default"
                                                overflow="hidden"
                                            >
                                                <Box
                                                    position="absolute"
                                                    top={0}
                                                    left={0}
                                                    h="100%"
                                                    w={idx < activeIdx ? '100%' : '0%'}
                                                    bg="brand.solid"
                                                    borderRadius="full"
                                                    transition={`width ${STEPPER_SEGMENT_S}s ease ${stepperLineDelayS(idx, fromIdx, activeIdx)}s`}
                                                />
                                            </Box>
                                        );
                                    }
                                    return elements;
                                });
                            })()}
                        </Flex>
                        <HStack spacing={2} flexShrink={0}>
                            <TableSettingsButton
                                settings={tableSettings}
                                onUpdateSettings={updateTableSettings}
                            />
                            {selectedStatus === 'quoting' && orderDetail.quoting.length > 0 && (
                                <Tooltip label="Full screen" placement="top">
                                    <YaxleIconButton
                                        aria-label="Full screen"
                                        icon={<YaxleIcon name="expand" size="sm" />}
                                        size="toolbar"
                                        variant="ghost"
                                        colorScheme="gray"
                                        color="fg.muted"
                                        onClick={() => setIsQuotingFullScreen(true)}
                                    />
                                </Tooltip>
                            )}
                            {selectedStatus === 'selection' && orderDetail.selection.length > 0 && (
                                <Tooltip label="Full screen" placement="top">
                                    <YaxleIconButton
                                        aria-label="Full screen"
                                        icon={<YaxleIcon name="expand" size="sm" />}
                                        size="toolbar"
                                        variant="ghost"
                                        colorScheme="gray"
                                        color="fg.muted"
                                        onClick={() => setIsSelectionFullScreen(true)}
                                    />
                                </Tooltip>
                            )}
                            {selectedStatus === 'ordered' && orderDetail.ordered.length > 0 && (
                                <Tooltip label="Full screen" placement="top">
                                    <YaxleIconButton
                                        aria-label="Full screen"
                                        icon={<YaxleIcon name="expand" size="sm" />}
                                        size="toolbar"
                                        variant="ghost"
                                        colorScheme="gray"
                                        color="fg.muted"
                                        onClick={() => setIsOrderedFullScreen(true)}
                                    />
                                </Tooltip>
                            )}
                            <RefreshButton
                                refetch={handleManualRefresh}
                                size="sm"
                                isLoading={isManualRefreshing}
                            />
                        </HStack>
                    </Flex>

                    {/* MAIN CONTENT AREA (scrollable) */}
                    <Box
                        flex={1}
                        minH={0}
                        overflowY="auto"
                        position="relative"
                        px={6}
                        pb={6}
                        sx={{
                            ...tableSettingsSx,
                            '&::-webkit-scrollbar': { width: '8px' },
                            '&::-webkit-scrollbar-track': { background: 'transparent' },
                            '&::-webkit-scrollbar-thumb': { background: 'gray.300', borderRadius: '4px' },
                            '&::-webkit-scrollbar-thumb:hover': { background: 'gray.400' },
                            scrollbarWidth: 'thin',
                            scrollbarColor: 'gray.300 transparent',
                        }}
                    >
                        <Box pt={4}>
                            {/* Summary boxes – shown on selection and ordered tabs */}
                            {(selectedStatus === 'selection' || selectedStatus === 'ordered') && (
                                <HStack spacing={3} mb={4} align="stretch">
                                    <Flex flex={1} align="center" gap={2.5} bg="white" py={2} px={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                                        <Flex align="center" justify="center" w="32px" h="32px" borderRadius="full" bg="gray.100" flexShrink={0}>
                                            <Icon as={FiPackage as React.ElementType} boxSize={4} color="brand.solid" />
                                        </Flex>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.500" whiteSpace="nowrap">Parts Ordered</Text>
                                        <Text fontSize="md" fontWeight="bold" color="gray.900" ml="auto" whiteSpace="nowrap">
                                            {orderDetail.partsOrdered} / {orderDetail.totalParts}
                                        </Text>
                                    </Flex>
                                    <Flex flex={1} align="center" gap={2.5} bg="white" py={2} px={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                                        <Flex align="center" justify="center" w="32px" h="32px" borderRadius="full" bg="gray.100" flexShrink={0}>
                                            <Icon as={FiDollarSign as React.ElementType} boxSize={4} color="brand.solid" />
                                        </Flex>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.500" whiteSpace="nowrap">Order Value</Text>
                                        <Text fontSize="md" fontWeight="bold" color="gray.900" ml="auto" whiteSpace="nowrap">
                                            {formatPrice(orderDetail.orderValue)}
                                        </Text>
                                    </Flex>
                                    <Flex flex={1} align="center" gap={2.5} bg="white" py={2} px={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                                        <Flex align="center" justify="center" w="32px" h="32px" borderRadius="full" bg="gray.100" flexShrink={0}>
                                            <Icon as={FiPercent as React.ElementType} boxSize={4} color="brand.solid" />
                                        </Flex>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.500" whiteSpace="nowrap">Gross Margin</Text>
                                        <Box ml="auto">
                                            <GrossMarginSummaryValue
                                                grossMargin={orderDetail.grossMargin}
                                                minThreshold={orderDetail.minGrossMarginThreshold}
                                                size="sm"
                                                align="right"
                                            />
                                        </Box>
                                    </Flex>
                                    <Flex flex={1} align="center" gap={2.5} bg="white" py={2} px={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                                        <Flex align="center" justify="center" w="32px" h="32px" borderRadius="full" bg="gray.100" flexShrink={0}>
                                            <Icon as={FiClock as React.ElementType} boxSize={4} color="brand.solid" />
                                        </Flex>
                                        <Text fontSize="sm" fontWeight="medium" color="gray.500" whiteSpace="nowrap">{deliveryLabel}</Text>
                                        <Text fontSize="md" fontWeight="bold" color="gray.900" ml="auto" whiteSpace="nowrap">
                                            {deliveryValue}
                                        </Text>
                                    </Flex>
                                </HStack>
                            )}

                            {/* Re-keying summary boxes */}
                            {selectedStatus === 'rekey' && orderDetail.rekey && (
                                <HStack spacing={3} mb={4}>
                                    <Flex flex={1} align="center" gap={2.5} bg="white" py={2} px={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                                        <Flex align="center" justify="center" w="32px" h="32px" borderRadius="full" bg="gray.100" flexShrink={0}>
                                            <Icon as={FiCheckCircle as React.ElementType} boxSize={4} color="brand.solid" />
                                        </Flex>
                                        <Text fontSize="xs" fontWeight="medium" color="gray.500" whiteSpace="nowrap">Parts Confirmed</Text>
                                        <Text fontSize="md" fontWeight="bold" color="gray.900" ml="auto">
                                            {orderDetail.rekey.partsConfirmed}
                                        </Text>
                                    </Flex>
                                    <Flex flex={1} align="center" gap={2.5} bg="white" py={2} px={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                                        <Flex align="center" justify="center" w="32px" h="32px" borderRadius="full" bg="gray.100" flexShrink={0}>
                                            <Icon as={FiRefreshCw as React.ElementType} boxSize={4} color="brand.solid" />
                                        </Flex>
                                        <Text fontSize="xs" fontWeight="medium" color="gray.500" whiteSpace="nowrap">Parts Re-Keyed</Text>
                                        <Text fontSize="md" fontWeight="bold" color="gray.900" ml="auto">
                                            {orderDetail.rekey.partsReKeyed}
                                        </Text>
                                    </Flex>
                                    <Flex flex={1} align="center" gap={2.5} bg="white" py={2} px={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                                        <Flex align="center" justify="center" w="32px" h="32px" borderRadius="full" bg="gray.100" flexShrink={0}>
                                            <Icon as={TbFlag as React.ElementType} boxSize={4} color="brand.solid" />
                                        </Flex>
                                        <Text fontSize="xs" fontWeight="medium" color="gray.500" whiteSpace="nowrap">
                                            {orderDetail.rekey.status === 'needed' ? 'Re-key Needed' : 'Parts Flagged'}
                                        </Text>
                                        <Text fontSize="md" fontWeight="bold" color="gray.900" ml="auto">
                                            {orderDetail.rekey.partsFlagged}
                                        </Text>
                                    </Flex>
                                    {orderDetail.rekey.status !== 'needed' && (
                                        <Flex flex={1} align="center" gap={2.5} bg="white" py={2} px={3} borderRadius="lg" borderWidth="1px" borderColor="gray.200">
                                            <Flex align="center" justify="center" w="32px" h="32px" borderRadius="full" bg="gray.100" flexShrink={0}>
                                                <Icon as={TbAlertTriangle as React.ElementType} boxSize={4} color="brand.solid" />
                                            </Flex>
                                            <Text fontSize="xs" fontWeight="medium" color="gray.500" whiteSpace="nowrap">Advisor Parts</Text>
                                            <Text fontSize="md" fontWeight="bold" color="gray.900" ml="auto">
                                                {orderDetail.rekey.partsAdvisor}
                                            </Text>
                                        </Flex>
                                    )}
                                </HStack>
                            )}

                            {selectedStatus === 'newParts' && (
                                <NewPartsSection
                                    parts={orderDetail.newParts.parts}
                                    orderId={id!}
                                    orderLocationId={orderDetail.locationId}
                                    orderLocationName={orderDetail.locationName}
                                    vin={orderDetail.vin}
                                    ems={orderDetail.ems}
                                    onOpenExportHistory={() => setActiveDrawer('emsHistory')}
                                    refetch={refreshOrder}
                                />
                            )}

                            {selectedStatus === 'quoting' && (
                                <QuotingTabs
                                    tabs={orderDetail.quoting}
                                    refetch={refreshOrder}
                                    orderId={id!}
                                    vin={orderDetail.vin}
                                />
                            )}

                            {selectedStatus === 'selection' && (
                                <SelectionTabs
                                    tabs={orderDetail.selection}
                                    orderId={id!}
                                    vin={orderDetail.vin}
                                    refetch={refreshOrder}
                                    preOrderBackorder={orderDetail.preOrderBackorder}
                                    needsSourcePartsMessage={orderDetail.needsSourcePartsMessage}
                                    quoteRequestErrors={orderDetail.quoteRequestErrors}
                                    potentialTotal={orderDetail.potentialTotal}
                                    quoteRequestAllowedAutoOrder={orderDetail.quoteRequestAllowedAutoOrder}
                                    supplierNotes={orderDetail.supplierNotes}
                                    quotesOnly={orderDetail.quotesOnly}
                                    isEvOrder={orderDetail.isEvOrder}
                                    canAttemptPnChange={orderDetail.canAttemptPnChange}
                                    oemCertificationPriceDifference={orderDetail.oemCertificationPriceDifference}
                                    scheduledOrderPlacement={orderDetail.scheduledOrderPlacement}
                                    drpJob={orderDetail.drpJob}
                                />
                            )}

                            {selectedStatus === 'rekey' && orderDetail.rekey && (
                                <RekeyTabs
                                    rekeyData={orderDetail.rekey}
                                    orderId={id!}
                                    drpJob={orderDetail.drpJob}
                                />
                            )}

                            {selectedStatus === 'ordered' && (
                                <OrderedTabs
                                    tabs={orderDetail.ordered}
                                    refetch={refreshOrder}
                                    vin={orderDetail.vin}
                                    supplierNotes={orderDetail.supplierNotes}
                                    supplierMessageCounts={orderDetail.supplierMessageCounts}
                                    locationId={orderDetail.locationId}
                                    roNumber={orderDetail.roNumber}
                                    vehicleMake={orderDetail.vehicleMake}
                                    quotesOnly={orderDetail.quotesOnly}
                                    onOpenSupplierCommunications={(supplierOrderId, initialTab) => {
                                        openSupplierCommunications({
                                            initialTab,
                                            supplierOrderId,
                                        });
                                    }}
                                />
                            )}
                        </Box>
                    </Box>

                </Box>
                {/* ── END LEFT COLUMN ─────────────────────────────── */}

                {/* ── SIDEBAR TOGGLE (vertical bar → arrow on hover) ── */}
                {isSidebarVisible && (
                    <Tooltip label="Collapse" placement="left" openDelay={300}>
                        <Flex
                            className="partbay-customer-sidebar"
                            position="absolute"
                            right="280px"
                            top={0}
                            transform="translateX(50%)"
                            zIndex={20}
                            align="center"
                            justify="center"
                            w="16px"
                            h="100%"
                            cursor="pointer"
                            onClick={() => setIsSidebarOpen(false)}
                            _hover={{ bg: 'blackAlpha.50' }}
                            role="group"
                        >
                            <Box
                                w="3px"
                                h="24px"
                                bg="gray.300"
                                borderRadius="full"
                                transition="all 150ms ease"
                                _groupHover={{ opacity: 0 }}
                            />
                            <Flex
                                position="absolute"
                                align="center"
                                justify="center"
                                opacity={0}
                                transition="all 150ms ease"
                                _groupHover={{ opacity: 1 }}
                                color="gray.500"
                            >
                                <Icon as={TbChevronRight as React.ElementType} w={4} h={4} />
                            </Flex>
                        </Flex>
                    </Tooltip>
                )}

                {/* ── RIGHT SIDEBAR ────────────────────────────────── */}
                <Box
                    className="partbay-customer-sidebar"
                    w={isSidebarVisible ? "280px" : isAnyDrawerOpen ? "0px" : "20px"}
                    flexShrink={0}
                    borderLeftWidth={isAnyDrawerOpen ? "0" : "1px"}
                    borderLeftStyle="solid"
                    borderColor="gray.200"
                    overflow="hidden"
                    bg={isSidebarVisible ? "white" : "gray.100"}
                    transition={suppressSidebarTransition ? "none" : "width 200ms ease, background-color 200ms ease"}
                    cursor={isSidebarRailInteractive ? "pointer" : "default"}
                    onClick={isSidebarRailInteractive ? () => setIsSidebarOpen(true) : undefined}
                    _hover={isSidebarRailInteractive ? { bg: 'gray.200' } : undefined}
                    position="relative"
                >
                    {/* Arrow icon when the user collapsed the sidebar (hidden while a
                        drawer forces the collapse, since the rail is inert then). */}
                    {isSidebarRailInteractive && (
                        <Flex
                            position="absolute"
                            top="50%"
                            left="50%"
                            transform="translate(-50%, -50%)"
                            color="gray.400"
                            _groupHover={{ color: 'gray.600' }}
                        >
                            <Icon as={TbChevronLeft as React.ElementType} w={3.5} h={3.5} />
                        </Flex>
                    )}
                    {/* Inner content — fixed at 280px so it doesn't reflow. Flex column so
                        the activity & notification sections split the remaining height
                        equally. */}
                    <Box
                        w="280px"
                        h="100%"
                        display="flex"
                        flexDirection="column"
                        overflow="hidden"
                        visibility={isSidebarVisible ? "visible" : "hidden"}
                        opacity={isSidebarVisible ? 1 : 0}
                        transition={suppressSidebarTransition ? "none" : "opacity 150ms ease, visibility 150ms ease"}
                    >

                        {/* ── Section 1: Order Info ── */}
                        <Box p={4} m={3} bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" flexShrink={0}>
                            <VStack align="stretch" spacing={3}>

                                {/* Customer */}
                                <SidebarField label="Customer">
                                    <Text fontSize="sm" color="gray.800">
                                        {orderDetail.customer.includes(' ')
                                            ? `${orderDetail.customer.split(' ').slice(-1)[0]}, ${orderDetail.customer.split(' ').slice(0, -1).join(' ')}`
                                            : orderDetail.customer}
                                    </Text>
                                </SidebarField>

                                {/* Claim # */}
                                <SidebarField label="Claim #">
                                    <HStack spacing={1} align="center">
                                        {orderDetail.claimNumber ? (
                                            <>
                                                <CopyTooltip isCopied={isCopied('claim')}>
                                                    <Text fontSize="sm" color="gray.800">{orderDetail.claimNumber}</Text>
                                                </CopyTooltip>
                                                <IconButton
                                                    aria-label="Copy claim number"
                                                    icon={<Icon as={TbCopy as React.ElementType} w={3} h={3} />}
                                                    size="xs"
                                                    variant="ghost"
                                                    onClick={(e) => copyToClipboard(orderDetail.claimNumber!, 'claim', 'Claim #', e)}
                                                    p={1}
                                                    minW="auto"
                                                    h="auto"
                                                    color="gray.400"
                                                    _hover={{ color: 'gray.600' }}
                                                    flexShrink={0}
                                                />
                                            </>
                                        ) : (
                                            <Text fontSize="sm" color="gray.800">—</Text>
                                        )}
                                    </HStack>
                                </SidebarField>

                                {/* Mileage */}
                                <SidebarField label="Mileage">
                                    <Text fontSize="sm" color="gray.800">
                                        {orderDetail.mileage != null ? orderDetail.mileage.toLocaleString() : '—'}
                                    </Text>
                                </SidebarField>

                                {/* Date Created */}
                                <SidebarField label="Date Created">
                                    <Text fontSize="sm" color="gray.800">
                                        {formatDate(orderDetail.timeCreated)}, {formatTime(orderDetail.timeCreated)}
                                    </Text>
                                </SidebarField>

                            </VStack>
                        </Box>

                        {/* ── Section 2: Recent Activity ── */}
                        <Box p={4} m={3} bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" flex={1} minH={0} display="flex" flexDirection="column" overflow="hidden">
                            <Flex justify="space-between" align="center" mb={3} flexShrink={0}>
                                <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                                    Recent Activity
                                </Text>
                                <Text
                                    fontSize="xs"
                                    color="brand.solid"
                                    cursor="pointer"
                                    fontWeight="medium"
                                    _hover={{ textDecoration: 'underline' }}
                                    onClick={() => setActiveDrawer('activity')}
                                >
                                    View Activity
                                </Text>
                            </Flex>
                            <Box flex={1} minH={0} overflowY="auto" sx={SIDEBAR_SCROLLBAR_SX}>
                                {!orderDetail.activityLog?.logs || orderDetail.activityLog.logs.length === 0 ? (
                                    <Text fontSize="sm" color="gray.400" fontStyle="italic">
                                        No activity yet
                                    </Text>
                                ) : (
                                    <VStack spacing={0} align="stretch">
                                        {orderDetail.activityLog.logs.map((log, idx, arr) => {
                                            const isLast = idx === arr.length - 1;
                                            // Carl-initiated rows override to partbay blue for the node, line,
                                            // and swap the event icon for the Carl sparkle (matches ActivityLogDrawer).
                                            const colorFor = (l: typeof log) =>
                                                l.source === 'carl-agent' ? 'brand.solid' : (EVENT_TYPE_COLORS[l.eventType] ?? 'brand.solid');
                                            const viaCarl = log.source === 'carl-agent';
                                            const nodeColor = colorFor(log);
                                            const nextLog = arr[idx + 1];
                                            const lineColor = nextLog ? colorFor(nextLog) : nodeColor;
                                            return (
                                                <Flex key={log._id} align="stretch">
                                                    {/* Color-coded numberline + icon node */}
                                                    <Flex direction="column" align="center" w="20px" flexShrink={0}>
                                                        <Tooltip
                                                            label="w/ Carl Agent"
                                                            placement="top"
                                                            hasArrow
                                                            openDelay={200}
                                                            isDisabled={!viaCarl}
                                                        >
                                                            <Flex
                                                                w="20px"
                                                                h="20px"
                                                                borderRadius="full"
                                                                bg={viaCarl ? 'brand.subtle' : (EVENT_TYPE_ICON_BG[log.eventType] ?? 'bg.subtle')}
                                                                align="center"
                                                                justify="center"
                                                                flexShrink={0}
                                                            >
                                                                <EventTypeIcon
                                                                    eventType={log.eventType}
                                                                    viaCarl={viaCarl}
                                                                    size="0.625rem"
                                                                />
                                                            </Flex>
                                                        </Tooltip>
                                                        {!isLast && (
                                                            <Box
                                                                flex={1}
                                                                minH="6px"
                                                                borderLeftWidth="1px"
                                                                borderLeftStyle="solid"
                                                                borderColor={lineColor}
                                                            />
                                                        )}
                                                    </Flex>
                                                    <Box flex={1} minW={0} pl={2} pb={isLast ? 0 : 3}>
                                                        <Text fontSize="xs" color="gray.700" noOfLines={1}>
                                                            <Text as="span" fontWeight="semibold">
                                                                {EVENT_TYPE_DISPLAY_LABELS[log.eventType]
                                                                    || log.eventType.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                                                            </Text>
                                                            {' '}
                                                            <Text as="span" color="gray.400">
                                                                {log.userName
                                                                    ? `${log.userName.firstName} ${log.userName.lastName}`
                                                                    : brandCopy('PartBay')}
                                                            </Text>
                                                        </Text>
                                                        {log.eventType === 'workflow-cancelled' && log.metadata?.Workflow && (
                                                            <Text textStyle="caption" color="fg.muted" noOfLines={1}>
                                                                {log.metadata.Workflow}
                                                                {log.metadata.Source ? ` · ${getActivitySourceLabel(log.metadata.Source)}` : ''}
                                                            </Text>
                                                        )}
                                                        <Text fontSize="10px" color="gray.400">
                                                            {(() => {
                                                                const d = new Date(log.timestamp);
                                                                const day = String(d.getDate()).padStart(2, '0');
                                                                const month = String(d.getMonth() + 1).padStart(2, '0');
                                                                const time = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
                                                                return `${month}/${day} • ${time}`;
                                                            })()}
                                                        </Text>
                                                    </Box>
                                                </Flex>
                                            );
                                        })}
                                    </VStack>
                                )}
                            </Box>
                        </Box>

                        {/* ── Section 3: Notifications ── */}
                        <Box p={4} m={3} bg="white" borderRadius="lg" border="1px solid" borderColor="gray.200" flex={1} minH={0} display="flex" flexDirection="column" overflow="hidden">
                            <Flex justify="space-between" align="center" mb={3} flexShrink={0}>
                                <Text fontSize="xs" fontWeight="semibold" color="gray.400" textTransform="uppercase" letterSpacing="wider">
                                    Notifications
                                </Text>
                                <Text
                                    fontSize="xs"
                                    color="brand.solid"
                                    cursor="pointer"
                                    fontWeight="medium"
                                    _hover={{ textDecoration: 'underline' }}
                                    onClick={() => setActiveDrawer('notifications')}
                                >
                                    View All
                                </Text>
                            </Flex>
                            <Box flex={1} minH={0} overflowY="auto" sx={SIDEBAR_SCROLLBAR_SX}>
                                {orderNotifications.length === 0 ? (
                                    <Text fontSize="sm" color="gray.400" fontStyle="italic">
                                        No notifications yet
                                    </Text>
                                ) : (
                                    <VStack spacing={0} align="stretch">
                                        {orderNotifications.map((notification, idx, arr) => {
                                            const { icon, color, bg } = getNotificationIcon(notification.type);
                                            // Muted types never toast or hit the bell drawer, but stay
                                            // visible here as the order's audit trail (ENG-2136).
                                            const muted = isNotificationTypeMuted(
                                                notification.type,
                                                orderNotificationsData?.notificationsEnabled,
                                                orderNotificationsData?.enabledNotificationTypes,
                                            );
                                            const isLast = idx === arr.length - 1;
                                            const nextNotification = arr[idx + 1];
                                            const lineColor = nextNotification
                                                ? getNotificationIcon(nextNotification.type).color
                                                : color;
                                            return (
                                                <Flex
                                                    key={notification._id}
                                                    align="stretch"
                                                    cursor="pointer"
                                                    onClick={() => setActiveDrawer('notifications')}
                                                    _hover={{ opacity: 0.8 }}
                                                >
                                                    {/* Color-coded numberline + icon node */}
                                                    <Flex direction="column" align="center" w="20px" flexShrink={0}>
                                                        <Flex
                                                            w="20px"
                                                            h="20px"
                                                            borderRadius="full"
                                                            bg={bg}
                                                            align="center"
                                                            justify="center"
                                                            flexShrink={0}
                                                        >
                                                            <Icon as={icon as React.ElementType} w={2.5} h={2.5} color={color} />
                                                        </Flex>
                                                        {!isLast && (
                                                            <Box
                                                                flex={1}
                                                                minH="6px"
                                                                borderLeftWidth="1px"
                                                                borderLeftStyle="solid"
                                                                borderColor={lineColor}
                                                            />
                                                        )}
                                                    </Flex>
                                                    <Box flex={1} minW={0} pl={2} pb={isLast ? 0 : 3}>
                                                        <Flex align="center" gap={1} minW={0}>
                                                            <Text
                                                                fontSize="xs"
                                                                color="gray.700"
                                                                fontWeight={notification.read ? 'normal' : 'semibold'}
                                                                noOfLines={1}
                                                                minW={0}
                                                            >
                                                                {getNotificationItemMessage(notification)}
                                                            </Text>
                                                            {muted && (
                                                                <Tooltip
                                                                    label="Alerts for this type are turned off in your notification preferences"
                                                                    placement="top"
                                                                    hasArrow
                                                                    openDelay={300}
                                                                >
                                                                    <Box as="span" display="inline-flex" flexShrink={0}>
                                                                        <Icon as={TbBellOff as React.ElementType} boxSize={3} color="gray.400" />
                                                                    </Box>
                                                                </Tooltip>
                                                            )}
                                                        </Flex>
                                                        <Text fontSize="10px" color="gray.400">
                                                            {new Date(notification.timeCreated).toLocaleString('en-US', {
                                                                month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
                                                            })}
                                                        </Text>
                                                    </Box>
                                                    {!notification.read && (
                                                        <Box w="6px" h="6px" borderRadius="full" bg="brand.solid" mt="6px" flexShrink={0} />
                                                    )}
                                                </Flex>
                                            );
                                        })}
                                    </VStack>
                                )}
                            </Box>
                        </Box>

                    </Box>
                    {/* end inner scrollable content */}
                </Box>
                {/* ── END RIGHT SIDEBAR ────────────────────────────── */}

            </Box>

            {/* Chat, supplier notes & activity log drawers */}
            {orderDetail && id && (
                <>
                    {showAskCarl && isCarlDrawerOpen && (
                        <CarlChatDrawer
                            isOpen={isCarlDrawerOpen}
                            onClose={() => setIsCarlDrawerOpen(false)}
                            orderId={id}
                            roNumber={orderDetail.roNumber}
                            vehicleName={orderDetail.vehicleName}
                        />
                    )}
                    <OrderChatDrawer
                        isOpen={isChatOpen}
                        onClose={() => setActiveDrawer(null)}
                        hideOverlay
                        orderId={id}
                        mode="order"
                        notes={orderDetail.notes || []}
                        mentionNames={orderDetail.mentionNames}
                        initialComposeMode={chatComposeMode}
                        refetch={refreshOrder}
                    />
                    <SupplierNotesModal
                        isOpen={isSupplierNotesOpen}
                        onClose={() => setActiveDrawer(null)}
                        hideOverlay
                        orderId={id}
                        notes={orderDetail.supplierNotes || []}
                        supplierOrders={supplierOrderSummaries}
                        initialTab={supplierNotesInitialTab}
                        initialSupplierOrderId={supplierNotesInitialSupplierOrderId}
                    />
                    <ActivityLogDrawer
                        isOpen={isActivityLogOpen}
                        onClose={() => setActiveDrawer(null)}
                        orderId={id}
                        hideOverlay
                    />
                    <ExportHistoryDrawer
                        isOpen={isEmsHistoryOpen}
                        onClose={() => setActiveDrawer(null)}
                        ems={orderDetail.ems}
                        orderId={id}
                        onSynced={() => { void refreshOrder(); }}
                        hideOverlay
                    />
                    <OrderNotificationsDrawer
                        isOpen={isOrderNotificationsOpen}
                        onClose={() => setActiveDrawer(null)}
                        orderId={id}
                        onSelectTab={setSelectedStatus}
                        hideOverlay
                    />
                </>
            )}

            {/* Quoting Full Screen Mode */}
            {isQuotingFullScreen && orderDetail.quoting.length > 0 && (
                <QuotingFullScreen
                    tabs={orderDetail.quoting}
                    orderId={id!}
                    vin={orderDetail.vin}
                    refetch={refreshOrder}
                    onClose={() => setIsQuotingFullScreen(false)}
                    tableSettings={tableSettings}
                    onUpdateTableSettings={updateTableSettings}
                />
            )}

            {/* Selection Full Screen Mode */}
            {isSelectionFullScreen && orderDetail.selection.length > 0 && (
                <SelectionFullScreen
                    tabs={orderDetail.selection}
                    orderId={id!}
                    vin={orderDetail.vin}
                    refetch={refreshOrder}
                    isEvOrder={orderDetail.isEvOrder}
                    quotesOnly={orderDetail.quotesOnly}
                    potentialTotal={orderDetail.potentialTotal}
                    onClose={() => setIsSelectionFullScreen(false)}
                    tableSettings={tableSettings}
                    onUpdateTableSettings={updateTableSettings}
                    drpJob={orderDetail.drpJob}
                />
            )}
            {isOrderedFullScreen && orderDetail.ordered.length > 0 && (
                <OrderedFullScreen
                    tabs={orderDetail.ordered}
                    vin={orderDetail.vin}
                    onClose={() => setIsOrderedFullScreen(false)}
                    tableSettings={tableSettings}
                    onUpdateTableSettings={updateTableSettings}
                />
            )}

            <OrderWorkflowOverlay orderId={id || ''} />
        </>
    );
};

export default OrderDetail;
